/**
 * AC-1478: `useQueuePriorityHistogram(queueId, status, bins)` - TanStack Query
 * read hook for the priority histogram of a single queue.
 *
 * Fetches via `apiService.getQueuePriorityHistogram(queueId, status, bins)`
 * (AC-1477 endpoint). The response payload is contract-bound to the actual
 * server response (`status_filter`, `bin_strategy`, `bins[].label`) per the
 * shipped backend tests.
 *
 * ## Key shape
 *
 * `queryKeys.queuePriorityHistogram(queueId, status, bins)` produces
 * `['queue', Number(queueId), 'priority-histogram', status, bins]`. Status is
 * part of the key so each chip click routes to a fresh cache slot. The cache
 * is hierarchical under `queryKeys.queue(queueId)` so a single
 * `invalidateQueries(queryKeys.queue(id))` cascades to every histogram slot.
 *
 * ## Defaults
 *
 * - `status` defaults to `'PENDING'`. The queue details page opens to PENDING
 *   by default, so a no-arg call site gets the right slot.
 * - `bins` defaults to 20, matching the backend's default and the chart's
 *   visual capacity (a Recharts BarChart at typical width supports ~20 bars
 *   without crowding).
 *
 * ## Retry policy
 *
 * Inherits the QueryClient default (production: 1 retry; tests: no retries).
 * No per-hook override - the production client's defaults are tuned for users
 * (1 retry on transient network blips) and the test client's `retry: false`
 * default keeps tests deterministic. Adding a hook-level override here would
 * break that contract and re-introduce flaky test behavior.
 *
 * ## Test coverage
 *
 * `frontend/src/hooks/queries/__tests__/useQueuePriorityHistogram.test.tsx`
 * pins the call shape, default values, query key cache slot routing, and the
 * UseQueryResult shape.
 */

import { useQuery, UseQueryResult } from '@tanstack/react-query';

import apiService from '../../services/api';
import { HistogramStatus, PriorityHistogramResponse } from '../../types';
import { queryKeys } from './queryKeys';

export function useQueuePriorityHistogram(
  queueId: number,
  status: HistogramStatus = 'PENDING',
  bins: number = 20,
): UseQueryResult<PriorityHistogramResponse, Error> {
  // Treat undefined/null/0 queue IDs as "no queue selected yet". The component
  // ensures it always has a queue before mounting, but the guard keeps stray
  // call sites from firing GET /queues/0/priority-histogram.
  const hasId = queueId !== undefined && queueId !== null && Number(queueId) > 0;

  return useQuery<PriorityHistogramResponse, Error>({
    queryKey: queryKeys.queuePriorityHistogram(queueId, status, bins),
    queryFn: () =>
      apiService.getQueuePriorityHistogram(Number(queueId), status, bins),
    enabled: hasId,
  });
}

export default useQueuePriorityHistogram;
